import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import {
  createHackForNewSymptom,
  getAllSymptomsForAdmin,
  updateHackForSymptom,
} from '@domain/features/pd-hacks/pd-hacks-feature'
import type { PdSymptom } from '@domain/models/pd-hacks'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { usePdHackCategories } from '@presentation/hooks/usePdHackCategories'
import SeoHead from '@presentation/seo/SeoHead'

const CREATE_NEW_SYMPTOM_OPTION = '__create_new_symptom__'

interface HackFormState {
  name: string
  image: string
  michelsPdHacksVideoUrl: string
  pdHacksCategoryId: string
}

function createEmptyForm(): HackFormState {
  return {
    name: '',
    image: '',
    michelsPdHacksVideoUrl: '',
    pdHacksCategoryId: '',
  }
}

function toFormState(symptom: PdSymptom): HackFormState {
  return {
    name: symptom.name ?? '',
    image: symptom.image ?? '',
    michelsPdHacksVideoUrl: symptom.michelsPdHacksVideoUrl ?? '',
    pdHacksCategoryId: symptom.pdHacksCategoryId ?? '',
  }
}

export default function HackDetailEditPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const screenPath = `${location.pathname}${location.search || ''}`
  const { symptomId } = useParams<{ symptomId?: string }>()
  const isEditMode = Boolean(symptomId)
  const { user, loading: authLoading } = useAuthUser()
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = usePdHackCategories(true)

  const [symptoms, setSymptoms] = useState<PdSymptom[]>([])
  const [selectedSymptomId, setSelectedSymptomId] = useState<string>(
    symptomId ?? CREATE_NEW_SYMPTOM_OPTION,
  )
  const [form, setForm] = useState<HackFormState>(createEmptyForm())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dropdownSymptoms = useMemo(() => {
    if (isEditMode && symptomId) {
      return symptoms.filter((item) => item.uid === symptomId)
    }

    return symptoms.filter(
      (item) => !item.michelsPdHacksVideoUrl?.trim(),
    )
  }, [isEditMode, symptomId, symptoms])

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!user?.isAdmin) {
      navigate('/hacks', { replace: true })
    }
  }, [authLoading, navigate, user])

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        setError(null)
        const allSymptoms = await getAllSymptomsForAdmin()
        if (cancelled) {
          return
        }

        setSymptoms(allSymptoms)

        if (isEditMode && symptomId) {
          const currentSymptom = allSymptoms.find((item) => item.uid === symptomId)
          if (!currentSymptom) {
            setError('Selected symptom was not found.')
            setForm(createEmptyForm())
            return
          }
          setSelectedSymptomId(symptomId)
          setForm(toFormState(currentSymptom))
          return
        }

        setSelectedSymptomId(CREATE_NEW_SYMPTOM_OPTION)
        setForm(createEmptyForm())
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error ? loadError.message : 'Failed to load symptoms.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [isEditMode, symptomId])

  const inputClassName =
    'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0! disabled:cursor-not-allowed disabled:border-border-light disabled:bg-border-light disabled:text-text-muted'
  const sectionClassName = 'rounded-2xl border border-border-light bg-surface p-4'

  const handleInputChange = (
    key: keyof HackFormState,
    value: string,
  ) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const handleSymptomSelect = (nextSymptomId: string) => {
    setSelectedSymptomId(nextSymptomId)

    if (nextSymptomId === CREATE_NEW_SYMPTOM_OPTION) {
      setForm(createEmptyForm())
      return
    }

    const matchedSymptom = symptoms.find((item) => item.uid === nextSymptomId)
    if (matchedSymptom) {
      setForm(toFormState(matchedSymptom))
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const normalizedName = form.name.trim()
    const normalizedVideoUrl = form.michelsPdHacksVideoUrl.trim()
    const normalizedPdHacksCategoryId = form.pdHacksCategoryId.trim()
    if (!normalizedName) {
      setError('Symptom name is required.')
      return
    }
    if (!normalizedVideoUrl) {
      setError('YouTube URL is required.')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isEditMode && symptomId) {
        await updateHackForSymptom(symptomId, {
          image: form.image.trim() || undefined,
          michelsPdHacksVideoUrl: normalizedVideoUrl,
          pdHacksCategoryId:
            normalizedPdHacksCategoryId.length > 0 ? normalizedPdHacksCategoryId : '',
        })
        AnalyticsFeature.trackHackSubmit(screenPath, 'hack_edit', true)
        navigate(`/hacks/${symptomId}`)
        return
      }

      if (selectedSymptomId === CREATE_NEW_SYMPTOM_OPTION) {
        const createdId = await createHackForNewSymptom({
          name: normalizedName,
          image: form.image.trim() || undefined,
          michelsPdHacksVideoUrl: normalizedVideoUrl,
          pdHacksCategoryId: normalizedPdHacksCategoryId || undefined,
        })
        AnalyticsFeature.trackHackSubmit(screenPath, 'hack_create', true)
        navigate(`/hacks/${createdId}`)
        return
      }

      await updateHackForSymptom(selectedSymptomId, {
        image: form.image.trim() || undefined,
        michelsPdHacksVideoUrl: normalizedVideoUrl,
        pdHacksCategoryId:
          normalizedPdHacksCategoryId.length > 0 ? normalizedPdHacksCategoryId : '',
      })
      AnalyticsFeature.trackHackSubmit(screenPath, 'hack_attach_existing', true)
      navigate(`/hacks/${selectedSymptomId}`)
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : 'Failed to save hack.',
      )
    } finally {
      setSaving(false)
    }
  }

  if (authLoading || loading) {
    return <LoadingSpinner className="min-h-[50vh]" />
  }

  if (!user?.isAdmin) {
    return <LoadingSpinner className="min-h-[50vh]" />
  }

  const cancelTarget = isEditMode && symptomId ? `/hacks/${symptomId}` : '/hacks'

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <SeoHead
        title={isEditMode ? 'Edit Hack | Parkinson\'s Insights' : 'Create Hack | Parkinson\'s Insights'}
        canonicalPath={isEditMode && symptomId ? `/hacks/${symptomId}/edit` : '/hacks/create'}
        noindex
      />
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-text-primary">
          {isEditMode ? 'Edit Hack' : 'Create Hack'}
        </h1>
        <Link
          to={cancelTarget}
          className="text-sm font-semibold text-primary hover:text-primary-light"
        >
          Cancel
        </Link>
      </div>

      {error ? (
        <p className="mt-3 rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      ) : null}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <section className={sectionClassName}>
          <label className="block text-sm font-semibold text-text-secondary">
            Symptom
            <select
              value={selectedSymptomId}
              onChange={(event) => handleSymptomSelect(event.target.value)}
              className={`${inputClassName} mt-2`}
              disabled={isEditMode}
            >
              {!isEditMode ? (
                <option value={CREATE_NEW_SYMPTOM_OPTION}>Create new symptom</option>
              ) : null}
              {dropdownSymptoms.map((symptom) => (
                <option key={symptom.uid} value={symptom.uid}>
                  {symptom.name?.trim() || symptom.uid}
                </option>
              ))}
            </select>
          </label>
        </section>

        <section className={sectionClassName}>
          <label className="block text-sm font-semibold text-text-secondary">
            Symptom name
            <input
              value={form.name}
              onChange={(event) => handleInputChange('name', event.target.value)}
              className={`${inputClassName} mt-2`}
              disabled={isEditMode || selectedSymptomId !== CREATE_NEW_SYMPTOM_OPTION}
              required
            />
          </label>

          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            Symptom image URL
            <input
              value={form.image}
              onChange={(event) => handleInputChange('image', event.target.value)}
              className={`${inputClassName} mt-2`}
              placeholder="https://..."
            />
          </label>

          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            Hack category
            <select
              value={form.pdHacksCategoryId}
              onChange={(event) => handleInputChange('pdHacksCategoryId', event.target.value)}
              className={`${inputClassName} mt-2`}
              disabled={categoriesLoading}
            >
              <option value="">No category</option>
              {categories.map((category) => (
                <option key={category.uid} value={category.uid}>
                  {category.name}
                </option>
              ))}
            </select>
            {categoriesError ? (
              <span className="mt-1 block text-xs font-medium text-red-500">
                Unable to load categories: {categoriesError}
              </span>
            ) : null}
            {!categoriesLoading && !categoriesError && categories.length === 0 ? (
              <span className="mt-1 block text-xs font-medium text-text-muted">
                No active categories are available.
              </span>
            ) : null}
          </label>

          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            YouTube URL (Shorts)
            <input
              value={form.michelsPdHacksVideoUrl}
              onChange={(event) =>
                handleInputChange('michelsPdHacksVideoUrl', event.target.value)}
              className={`${inputClassName} mt-2`}
              placeholder="https://www.youtube.com/shorts/..."
              required
            />
          </label>
        </section>

        <div className="flex justify-center pt-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex min-w-36 items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-light disabled:opacity-65"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}
